import React from 'react';
import { CheckCircle, Lightbulb, Wrench, Droplets } from 'lucide-react';

export const ProcessSection = () => {
  const fallbackImages = {
    step1: `${process.env.PUBLIC_URL}/process_step_1.png`,
    step2: `${process.env.PUBLIC_URL}/process_step_2.png`,
    step3: `${process.env.PUBLIC_URL}/process_step_3.png`,
    step4: `${process.env.PUBLIC_URL}/process_step_4.png`
  };

  const steps = [
    {
      image: fallbackImages.step1,
      alt: 'Aquify specialist conducting an in-home water evaluation',
      icon: CheckCircle,
      title: 'Complimentary Water Evaluation',
      description: 'Schedule your complimentary in-home water quality assessment',
      color: '#0C4288'
    },
    {
      image: fallbackImages.step2,
      alt: 'Aquify specialist reviewing a customized water solution with homeowners',
      icon: Lightbulb,
      title: 'Custom Solution',
      description: 'Receive a personalized purification system recommendation',
      color: '#1571BA'
    },
    {
      image: fallbackImages.step3,
      alt: 'Professional installing and configuring a water treatment system',
      icon: Wrench,
      title: 'Professional Installation',
      description: 'Our certified technicians install your system with care',
      color: '#2A9AD8'
    },
    {
      image: fallbackImages.step4,
      alt: 'Family enjoying treated water at home',
      icon: Droplets,
      title: 'Enjoy Treated Water',
      description: 'Experience the difference of crystal-clear, healthy water',
      color: '#50BAED'
    }
  ];

  return (
    <section className="py-20" style={{ background: 'var(--master-gradient)' }}>
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            Simple <span className="text-[#90D8F5]">4-Step Process</span>
          </h2>
          <p className="text-xl max-w-3xl mx-auto text-white/80">
            Getting started with treated water is easier than you think
          </p>
        </div>

        {/* Desktop / Tablet View */}
        <div className="hidden md:block">
          <div className="relative">
            {/* Connecting Line with Gradient - Hidden on tablet, visible on lg */}
            <div
              className="hidden lg:block absolute top-28 h-1 bg-white/40"
              style={{
                left: '12.5%',
                right: '12.5%'
              }}
            ></div>

            {/* Steps */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-6 xl:gap-8 relative">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const wpImage = window.aquifyConfig?.processImages?.[`step${index + 1}`];
                return (
                  <div key={index} className="flex flex-col items-center text-center">
                    {/* Image Circle with Gradient Border */}
                    <div
                      className="w-56 h-56 rounded-full flex items-center justify-center shadow-xl mb-6 relative z-10 transition-transform duration-300 hover:scale-105 border-4 border-white/80 ring-2 ring-[#8EDAF4]/60"
                    >
                      <div className="w-full h-full rounded-full overflow-hidden"> {/* Inner spacing removed */}
                        <img
                          src={wpImage?.src || step.image}
                          srcSet={wpImage?.srcSet || undefined}
                          sizes={wpImage?.sizes || undefined}
                          width={wpImage?.width || undefined}
                          height={wpImage?.height || undefined}
                          alt={wpImage?.alt || step.alt}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover rounded-full"
                        />
                      </div>
                    </div>

                    {/* Icon */}
                    <div className="bg-white p-3 rounded-full shadow-lg mb-4 -mt-3 relative z-20" style={{ marginTop: '-2rem' }}>
                      <Icon className="h-6 w-6" style={{ color: step.color }} />
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold mb-3 text-white">{step.title}</h3>

                    {/* Description */}
                    <p className="leading-relaxed text-white/80">{step.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Mobile View - Stacked */}
        <div className="md:hidden space-y-16">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const wpImage = window.aquifyConfig?.processImages?.[`step${index + 1}`];
            return (
              <div key={index} className="relative flex flex-col items-center text-center">
                {/* Image Circle with Gradient Border */}
                <div
                  className="w-48 h-48 rounded-full flex items-center justify-center shadow-xl mb-4 relative z-10 border-4 border-white/80 ring-2 ring-[#8EDAF4]/60"
                >
                  <div className="w-full h-full rounded-full overflow-hidden">
                    <img
                      src={wpImage?.src || step.image}
                      srcSet={wpImage?.srcSet || undefined}
                      sizes={wpImage?.sizes || undefined}
                      width={wpImage?.width || undefined}
                      height={wpImage?.height || undefined}
                      alt={wpImage?.alt || step.alt}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover rounded-full"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="flex flex-col items-center relative z-20" style={{ marginTop: '-1.5rem' }}>
                  <div className="bg-white p-3 rounded-full shadow-lg mb-4">
                    <Icon className="h-6 w-6" style={{ color: step.color }} />
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-white">{step.title}</h3>
                  <p className="leading-relaxed px-4 text-white/80">{step.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
